import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Component for displaying relationships as clickable links with delete option
 */
const RelationshipDisplay = ({ relationships = [], entryId = null, onRelationshipChange = null }) => {
  const navigate = useNavigate();
  const [relationshipDetails, setRelationshipDetails] = useState([]);

  useEffect(() => {
    const loadRelationshipDetails = async () => {
      if (relationships.length === 0) {
        setRelationshipDetails([]);
        return;
      }

      try {
        const details = await Promise.all(
          relationships.map(async (rel) => {
            try {
              const entry = await wikiService.getWikiEntry(rel.entryId);
              return { 
                entryId: rel.entryId,
                relationshipType: rel.type, // The relationship type from rel.type
                title: entry.title,
                type: entry.type // The entry type (location, person, etc.)
              };
            } catch (error) {
              console.error(`Error loading relationship entry ${rel.entryId}:`, error);
              return null;
            }
          })
        );
        setRelationshipDetails(details.filter(d => d !== null));
      } catch (error) {
        console.error('Error loading relationship details:', error);
      }
    };

    loadRelationshipDetails();
  }, [relationships]);

  const handleDelete = async (targetEntryId) => {
    if (!entryId || !onRelationshipChange) return;
    
    try {
      await wikiService.removeRelationship(entryId, targetEntryId);
      onRelationshipChange();
    } catch (error) {
      console.error('Error removing relationship:', error);
    }
  };

  // Function to get display label for a relationship type
  const getRelationshipLabel = (type) => {
    const typeMap = {
      'related': 'Related To',
      'mentioned_in': 'Mentioned In',
      'appeared_in': 'Appeared In',
      'ally': 'Ally',
      'enemy': 'Enemy',
      'rival': 'Rival',
      'friend': 'Friend',
      'family': 'Family Member',
      'parent': 'Parent',
      'child': 'Child',
      'sibling': 'Sibling',
      'spouse': 'Spouse',
      'subordinate': 'Subordinate',
      'leader': 'Leader',
      'mentor': 'Mentor',
      'student': 'Student',
      'owns_item': 'Owns',
      'owns_location': 'Owns Location',
      'previously_owned': 'Previously Owned',
      'seeks': 'Seeks',
      'protects': 'Protects',
      'hunts': 'Hunts',
      'lives_in': 'Lives In',
      'works_at': 'Works At',
      'member_of': 'Member Of',
      'owned_by': 'Owned By',
      'previously_owned_by': 'Previously Owned By',
      'created_by': 'Created By',
      'discovered_by': 'Discovered By',
      'stolen_by': 'Stolen By',
      'sought_by': 'Sought By',
      'protected_by': 'Protected By',
      'located_at': 'Located At',
      'held_by': 'Held By',
      'hidden_in': 'Hidden In',
      'found_in': 'Found In',
      'part_of': 'Part Of',
      'contains': 'Contains',
      'connected_to': 'Connected To',
      'near': 'Near',
      'parent_location': 'Within',
      'child_location': 'Contains Location',
      'ruled_by': 'Ruled By',
      'threatened_by': 'Threatened By',
      'home_to': 'Home To',
      'workplace_of': 'Workplace Of',
      'headquarters_of': 'Headquarters Of',
      'enemy_of': 'Enemy Of',
      'hunted_by': 'Hunted By',
      'serves': 'Serves',
      'commanded_by': 'Commanded By',
      'guards': 'Guards',
      'inhabits': 'Inhabits',
      'summoned_by': 'Summoned By',
      'ally_of': 'Ally Of',
      'defeated_by': 'Defeated By',
      'possesses': 'Possesses',
      'encountered_at': 'Encountered At',
      'encounter_with': 'Encounter With',
      'has_member': 'Has Member',
      'led_by': 'Led By',
      'leads': 'Leads',
      'founded': 'Founded',
      'founded_by': 'Founded By',
      'allied_with': 'Allied With',
      'rival_of': 'Rival Of',
      'at_war_with': 'At War With',
      'controls': 'Controls',
      'controlled_by': 'Controlled By',
      'based_in': 'Based In',
      'owns': 'Owns',
      'owned_by_faction': 'Owned By',
      'sponsors': 'Sponsors',
      'sponsored_by': 'Sponsored By',
      'opposes': 'Opposes',
      'opposed_by': 'Opposed By',
      'parent_faction': 'Parent Faction',
      'subfaction_of': 'Subfaction Of',
    };
    return typeMap[type] || type;
  };

  const getTypeEmoji = (type) => {
    switch (type) {
      case 'location':
        return '📍';
      case 'person':
        return '👥';
      case 'item':
        return '⚔️';
      case 'monster':
        return '👹';
      case 'faction':
        return '🏰';
      default:
        return '📖';
    }
  };

  if (relationshipDetails.length === 0) {
    return (
      <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontStyle: 'italic' }}>
        No relationships
      </Typography>
    );
  }

  return (
    <List sx={{ width: '100%', bgcolor: wikiTheme.dark, borderRadius: 1 }}>
      {relationshipDetails.map((rel) => (
        <ListItem 
          key={rel.entryId} 
          disablePadding
          secondaryAction={
            entryId && onRelationshipChange ? (
              <IconButton 
                edge="end" 
                aria-label="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(rel.entryId);
                }}
                sx={{
                  color: wikiTheme.tertiary,
                  '&:hover': {
                    color: '#ff6b6b',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            ) : null
          }
          sx={{
            borderBottom: `1px solid ${wikiTheme.lightGray}`,
            '&:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          <ListItemButton
            onClick={() => navigate(`/wiki/${rel.entryId}`)}
            sx={{
              py: 1,
              pr: entryId && onRelationshipChange ? 6 : 2,
              '&:hover': {
                backgroundColor: wikiTheme.mediumGray,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography 
                component="span" 
                sx={{ 
                  color: wikiTheme.primary, 
                  fontFamily: wikiTheme.font,
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                }}
              >
                {getRelationshipLabel(rel.relationshipType)}:
              </Typography>
              <Typography component="span" sx={{ fontSize: '1rem' }}>
                {getTypeEmoji(rel.type)}
              </Typography>
              <Typography 
                component="span" 
                sx={{ 
                  color: wikiTheme.secondary, 
                  fontFamily: wikiTheme.font,
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                }}
              >
                {rel.title}
              </Typography>
            </Box>
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

export default RelationshipDisplay;
